<template>
    <div class="relative form-date-picker">
        <FlatPickrWrapper v-model:value="computedValue" v-model:show="expandPicker" :disabled="disabled" :config="flatpickrConfig" :position="position" :show-overlay="showOverlay" auto-width mode="single">
            <div class="relative overflow-hidden">
                <CustomInput
                    ref="input-field"
                    v-model:value="inputValue"
                    :required="required"
                    :show-required-indication="required"
                    :rules="[]"
                    input-type="text"
                    :label="label"
                    :aria-label="ariaLabel || label || undefined"
                    :details="details"
                    :disabled="disabled"
                    readonly
                    cursor="cursor-pointer"
                    @submit="$emit('submit')"
                    @focus="expandPicker = true"
                    @keydown.esc.stop="onEscape"
                ></CustomInput>
                <FadeTransition>
                    <span
                        v-if="resetable && !required && computedValue"
                        :class="[{ 'cursor-default opacity-40': disabled, 'cursor-pointer': !disabled }, details ? '-mt-3.5' : '-mt-2']"
                        class="absolute h-full top-0 right-14 grid content-center"
                        @click="onClearClicked"
                    >
                        <Icon class="m-auto" :src="IconSource.Close" />
                    </span>
                </FadeTransition>
                <span :class="[{ 'cursor-default opacity-40': disabled }, details ? '-mt-3.5' : '-mt-2']" class="absolute h-full top-0 right-5 grid content-center" @click="focusInputField()">
                    <Icon class="m-auto" :src="IconSource.DatePicker" />
                </span>
            </div>
        </FlatPickrWrapper>
    </div>
</template>

<script lang="ts">
import type { FieldDetails } from '@/mixins/validatableField';
import { defineComponent } from 'vue';
import FlatPickrWrapper from '@/components/ui/FlatpickrWrapper.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import FadeTransition from '../transitions/FadeTransition.vue';

export default defineComponent({
    components: { FlatPickrWrapper, CustomInput, FadeTransition },
    props: {
        value: {
            type: String as () => string | null,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        // Screen-reader name for the input when no visible `label` is shown (e.g. a compact table
        // cell, where a visible label would re-add the vertical space the layout was tightened to
        // remove) - falls back to `label` itself when one is given, so callers only need to set this
        // on label-less usages.
        ariaLabel: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        resetable: {
            type: Boolean,
            default: true,
        },
        min: {
            type: String,
            default: '',
        },
        max: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        details: {
            type: Object as () => FieldDetails | undefined,
            default: undefined,
        },
        disableDates: {
            type: Array as () => (Date | ((date: Date) => boolean))[] | null,
            default: () => [],
        },
        position: {
            type: String,
            default: 'below',
            validator: (value: string) => ['below', 'above'].includes(value),
        },
        showOverlay: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:value', 'submit'],
    data() {
        return {
            expandPicker: false,
        };
    },
    computed: {
        flatpickrConfig(): any {
            return {
                maxDate: this.$props.max ? new Date(this.$props.max) : undefined,
                minDate: this.$props.min ? new Date(this.$props.min) : undefined,
                disable: this.$props.disableDates,
                position: this.$props.position,
            };
        },
        computedValue: {
            get(): string | null {
                return this.$props.value;
            },
            set(newDateString: string | null) {
                this.$emit('update:value', newDateString);
            },
        },
        inputValue(): string {
            if (this.computedValue) {
                return this.$d(new Date(this.computedValue), 'short');
            }
            return '';
        },
    },
    methods: {
        focusInputField() {
            (this.$refs['input-field'] as InstanceType<typeof CustomInput>).focusInput();
        },
        onEscape() {
            this.expandPicker = false;
            (document.activeElement as HTMLElement)?.blur();
        },
        onClearClicked() {
            if (!this.$props.disabled) {
                this.computedValue = null;
            }
        },
    },
});
</script>
<style>
.form-date-picker .flatpickr-wrapper {
    top: 2rem;
}
</style>
